import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import styled from 'styled-components';
import {
  DRAW_TOOLS,
  canvasToPdfPoint,
  createUserComponent,
  hitTestUserMarkup,
  isUserDrawnComponent,
  newUserMarkupId,
  paintUserMarkup,
  pdfPointToCanvas,
} from '../../utils/userMarkup.js';
import {
  collectComponentSnapPoints,
  collectEntitySnapPoints,
  dedupeSnapPoints,
  findSnapCandidates,
  buildSnapPointIndex,
  collectOrthoAlignmentCanvasPoints,
  applyOrthoSnap,
} from '../../utils/drawSnap.js';
import {
  completeOpenPolylineToRectangle,
  componentHasEditableVertices,
  componentSupportsVertexAddRemove,
  getComponentPdfVertices,
  getComponentCanvasVertices,
  getRightAngleRectangleCompletion,
  isPointInRectangleCompletionZone,
  applyOrthoLock,
  resolveOrthoAxis,
  hitTestComponentEdge,
  hitTestComponentVertex,
  insertVertexOnEdge,
  paintExtractedVertexHandles,
  paintHoverMarkup,
  paintVertexHandles,
  removeVertexAtIndex,
  translateComponentPdfVertices,
  updateComponentVertex,
} from '../../utils/markupGeometry.js';
import { paintArcBezierPreview } from '../../utils/arcPreview.js';
import {
  ARC_STYLES,
  buildCircularArcGeometry,
  paintArcGeometry,
} from '../../utils/arcGeometry.js';
import {
  canvasSegmentLengthPdf,
  formatLiveLength,
  paintRectDimensions,
  paintSegmentDimension,
  parseDrawLengthToPdfPoints,
  pointAtPdfDistance,
  resolveRotateDegreesFromInput,
} from '../../utils/drawMeasureFormat.js';
import { orthoAnchorForVertex } from '../../utils/vertexOrtho.js';
import { rotateComponent, mirrorComponent, offsetComponent, getOffsetArrowHandles, hitTestOffsetArrow, hoverOffsetArrowSide, parseOffsetDistanceInput } from '../../utils/markupTransform.js';

const Layer = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: ${(p) => (p.$interactive ? 'auto' : 'none')};
  cursor: ${(p) => p.$cursor || 'crosshair'};
  z-index: 4;
`;

function dist(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function paintOffsetArrow(ctx, x, y, angle, active = false) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.fillStyle = active ? '#16a34a' : '#2563eb';
  ctx.strokeStyle = active ? '#15803d' : '#1d4ed8';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(11, 0);
  ctx.lineTo(-7, 7);
  ctx.lineTo(-7, -7);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();
}

function supportsTypedLengthLock(tool) {
  return tool === DRAW_TOOLS.LINE || tool === DRAW_TOOLS.POLYLINE;
}

function orthoOriginKey(origin) {
  if (!origin) return null;
  return `${origin.x}:${origin.y}`;
}

function isMultiPointPolyTool(tool) {
  return tool === DRAW_TOOLS.POLYLINE || tool === DRAW_TOOLS.POLYGON;
}

function draftHintForTool(tool, pointCount, arcDrawMode = ARC_STYLES.CIRCULAR) {
  switch (tool) {
    case DRAW_TOOLS.LINE:
      return pointCount < 1
        ? 'Click start · type length + Enter · Shift = ortho'
        : 'Type length locks angle · Enter places · Shift = ortho';
    case DRAW_TOOLS.RECTANGLE:
      return pointCount < 1
        ? 'Click first corner, then opposite corner · Shift = square'
        : 'Click opposite corner · Esc to cancel';
    case DRAW_TOOLS.POLYLINE:
      return pointCount >= 2
        ? 'Esc or Enter finishes at last point'
        : 'Click points · Esc cancels · Enter finishes with 2+ points';
    case DRAW_TOOLS.POLYGON:
      return pointCount >= 3
        ? 'Esc or Enter closes at last point'
        : 'Click corners · Esc cancels · Enter closes with 3+ points';
    case DRAW_TOOLS.ARC:
      if (arcDrawMode === ARC_STYLES.BULGE) {
        return pointCount < 1
          ? 'Bulge arc: click start, then end, then control point'
          : pointCount < 2
            ? 'Click arc end point'
            : 'Click bulge control point to finish';
      }
      return pointCount < 1
        ? 'Circular arc: click center, then start on arc, then end'
        : pointCount < 2
          ? 'Click start point on arc (sets radius)'
          : 'Click end point on arc to finish';
    case DRAW_TOOLS.CIRCLE:
      return pointCount < 1
        ? 'Click center, then radius point'
        : 'Click radius point · Esc to cancel';
    case DRAW_TOOLS.FREEFORM:
      return 'Drag to draw · release to finish';
    default:
      return '';
  }
}

function findHitMarkup(pageMarkups, canvasX, canvasY, viewport) {
  for (let i = pageMarkups.length - 1; i >= 0; i -= 1) {
    if (hitTestUserMarkup(canvasX, canvasY, pageMarkups[i], viewport)) {
      return pageMarkups[i];
    }
  }
  return null;
}

function canFinishDraft(tool, pointCount) {
  if (tool === DRAW_TOOLS.POLYLINE) return pointCount >= 2;
  if (tool === DRAW_TOOLS.POLYGON) return pointCount >= 3;
  return false;
}

const UserDrawLayer = forwardRef(function UserDrawLayer({
  width,
  height,
  viewport,
  pageIndex = 0,
  sheetId = '',
  hatId = 'architect',
  drawTool = DRAW_TOOLS.SELECT,
  arcDrawMode = ARC_STYLES.CIRCULAR,
  userMarkups = [],
  selectedMarkupId = null,
  selectedExtractedComponent = null,
  modifyMode = null,
  onModifyModeChange,
  onRotateUserMarkup,
  onMirrorUserMarkup,
  onOffsetUserMarkup,
  scaleRatio = null,
  snapSources = null,
  snapEnabled = true,
  onMarkupComplete,
  onMarkupSelect,
  onMarkupUpdate,
  onExtractedGeometryUpdate,
  onMarkupHistoryCheckpoint,
  onDraftChange,
  onCommandCancel,
  dimensionInput = '',
  onDimensionInputChange,
  interactive = true,
  hidden = false,
}, ref) {
  const canvasRef = useRef(null);
  const [draftCanvasPts, setDraftCanvasPts] = useState([]);
  const [dragCurrent, setDragCurrent] = useState(null);
  const [freeformPts, setFreeformPts] = useState([]);
  const [snapIndicator, setSnapIndicator] = useState(null);
  const [hoveredMarkupId, setHoveredMarkupId] = useState(null);
  const [hoverVertexIndex, setHoverVertexIndex] = useState(-1);
  const [vertexDrag, setVertexDrag] = useState(null);
  const [objectDrag, setObjectDrag] = useState(null);
  const [dragPreview, setDragPreview] = useState(null);
  const [rotateSession, setRotateSession] = useState(null);
  const [rotateAim, setRotateAim] = useState(null);
  const [mirrorSession, setMirrorSession] = useState(null);
  const [mirrorAim, setMirrorAim] = useState(null);
  const rotateDegreesRef = useRef(0);
  const [hoverMarkupMove, setHoverMarkupMove] = useState(false);
  const [hoverRectangleComplete, setHoverRectangleComplete] = useState(false);
  const [offsetHandles, setOffsetHandles] = useState(null);
  const [offsetHoveredSide, setOffsetHoveredSide] = useState(null);
  const shiftRef = useRef(false);
  const copyRef = useRef(false);
  const [copyModifierActive, setCopyModifierActive] = useState(false);
  const orthoAxisRef = useRef(null);
  const orthoAxisOriginRef = useRef(null);
  const typedDirectionRef = useRef(null);
  const lastPointerCanvasRef = useRef(null);
  const snapCycleRef = useRef({ candidates: [], index: 0, anchor: null });
  const drawingRef = useRef(false);

  const pageIdx = pageIndex - 1;
  const pageMarkups = userMarkups.filter((m) => (m.pageIndex ?? 0) === pageIdx);

  const selectedMarkup = useMemo(
    () => pageMarkups.find((m) => m.id === selectedMarkupId) || null,
    [pageMarkups, selectedMarkupId],
  );

  const applyOffsetSide = useCallback((side) => {
    if (!selectedMarkup || side == null) return false;
    const n = Number(String(dimensionInput || '').trim());
    if (!Number.isFinite(n) || n === 0) return false;
    onOffsetUserMarkup?.(n * side, { copy: copyRef.current });
    onModifyModeChange?.(null);
    onDimensionInputChange?.('');
    setOffsetHandles(null);
    setOffsetHoveredSide(null);
    setDragPreview(null);
    return true;
  }, [
    dimensionInput, onDimensionInputChange, onModifyModeChange,
    onOffsetUserMarkup, selectedMarkup,
  ]);

  const editTarget = useMemo(() => {
    if (drawTool !== DRAW_TOOLS.SELECT) return null;
    if (selectedMarkup) return selectedMarkup;
    if (selectedExtractedComponent && componentHasEditableVertices(selectedExtractedComponent)) {
      return selectedExtractedComponent;
    }
    return null;
  }, [drawTool, selectedMarkup, selectedExtractedComponent]);

  const snapPoints = useMemo(() => {
    if (!snapEnabled || !snapSources) return [];
    const includeSelfVertices = modifyMode === 'mirror' || Boolean(mirrorSession);
    const components = [
      ...(snapSources.components || []),
      ...(snapSources.userMarkups || []).filter((m) => (
        includeSelfVertices ? true : m.id !== selectedMarkupId
      )),
    ];
    return buildSnapPointIndex(components, pageIdx, snapSources.pageEntities || []);
  }, [snapEnabled, snapSources, pageIdx, selectedMarkupId, modifyMode, mirrorSession]);

  const orthoAlignmentPoints = useMemo(() => {
    if (!viewport) return [];
    const extra = [...draftCanvasPts];
    if (editTarget) {
      const verts = getComponentCanvasVertices(editTarget, viewport);
      const skip = vertexDrag?.vertexIndex ?? -1;
      verts.forEach((v, i) => {
        if (i !== skip) extra.push(v);
      });
    }
    const exclude = vertexDrag && editTarget
      ? getComponentCanvasVertices(editTarget, viewport)[vertexDrag.vertexIndex]
      : null;
    return collectOrthoAlignmentCanvasPoints(snapPoints, viewport, extra, {
      excludeCanvasPoint: exclude,
    });
  }, [draftCanvasPts, editTarget, snapPoints, vertexDrag, viewport]);

  const ensureOrthoAxis = useCallback((origin, raw) => {
    if (!origin || !raw) return null;
    const key = orthoOriginKey(origin);
    if (orthoAxisOriginRef.current !== key || !orthoAxisRef.current) {
      orthoAxisRef.current = resolveOrthoAxis(origin, raw);
      orthoAxisOriginRef.current = key;
    }
    return orthoAxisRef.current;
  }, []);

  const resolvePoint = useCallback((raw, origin = null, {
    cycleSnap = false,
    shiftKey = null,
    orthoAlignPoints = null,
  } = {}) => {
    if (!raw) return null;
    let pt = raw;
    const orthoOn = origin && (shiftKey ?? shiftRef.current);

    if (orthoOn) {
      snapCycleRef.current = { candidates: [], index: 0, anchor: null };
      const axis = ensureOrthoAxis(origin, raw);
      const alignPts = orthoAlignPoints ?? orthoAlignmentPoints;
      const { pt: orthoPt, snap } = applyOrthoSnap(raw, origin, alignPts, 12, axis);
      pt = orthoPt;
      setSnapIndicator(snap);
      return pt;
    }

    if (snapEnabled && viewport) {
      const candidates = findSnapCandidates(pt, viewport, snapPoints);
      if (candidates.length) {
        const moved = snapCycleRef.current.anchor
          && dist(raw, snapCycleRef.current.anchor) > 16;
        if (moved || candidates.length !== snapCycleRef.current.candidates.length) {
          snapCycleRef.current = { candidates, index: 0, anchor: raw };
        } else if (cycleSnap) {
          snapCycleRef.current.index = (
            snapCycleRef.current.index + 1
          ) % candidates.length;
        }
        const pick = candidates[snapCycleRef.current.index];
        setSnapIndicator(pick.canvas);
        pt = pick.canvas;
      } else {
        snapCycleRef.current = { candidates: [], index: 0, anchor: null };
        setSnapIndicator(null);
      }
    }
    return pt;
  }, [ensureOrthoAxis, orthoAlignmentPoints, snapEnabled, snapPoints, viewport]);

  const resolveMirrorPoint = useCallback((raw) => {
    if (!raw) return null;
    let pt = resolvePoint(raw, null);
    if (mirrorSession?.axisStartCanvas && dist(pt, mirrorSession.axisStartCanvas) <= 12) {
      pt = mirrorSession.axisStartCanvas;
      setSnapIndicator(pt);
    }
    return pt;
  }, [mirrorSession, resolvePoint]);

  const resetDraft = useCallback(() => {
    setDraftCanvasPts([]);
    setDragCurrent(null);
    setFreeformPts([]);
    setSnapIndicator(null);
    drawingRef.current = false;
    typedDirectionRef.current = null;
    orthoAxisRef.current = null;
    orthoAxisOriginRef.current = null;
    lastPointerCanvasRef.current = null;
    onDimensionInputChange?.('');
  }, [onDimensionInputChange]);

  const cancelCommand = useCallback(() => {
    resetDraft();
    setVertexDrag(null);
    setDragPreview(null);
    setRotateSession(null);
    setRotateAim(null);
    setMirrorSession(null);
    setMirrorAim(null);
    setOffsetHandles(null);
    setOffsetHoveredSide(null);
    setObjectDrag(null);
    rotateDegreesRef.current = 0;
    onModifyModeChange?.(null);
    onCommandCancel?.();
  }, [onCommandCancel, onModifyModeChange, resetDraft]);

  const finishWithPdfPoints = useCallback((tool, pdfPoints) => {
    if (!pdfPoints?.length) return;
    const component = createUserComponent({
      tool,
      pdfPoints,
      arcStyle: tool === DRAW_TOOLS.ARC ? arcDrawMode : undefined,
      pageIndex: pageIdx,
      sheetId,
      hatId,
    });
    onMarkupComplete?.(component);
    resetDraft();
  }, [arcDrawMode, hatId, onMarkupComplete, pageIdx, resetDraft, sheetId]);

  const finishMultiPointDraft = useCallback(() => {
    if (!viewport) return;
    if (drawTool === DRAW_TOOLS.POLYLINE && draftCanvasPts.length >= 2) {
      const pdfPoints = draftCanvasPts.map((p) => canvasToPdfPoint(p.x, p.y, viewport));
      finishWithPdfPoints(drawTool, pdfPoints);
    } else if (drawTool === DRAW_TOOLS.POLYGON && draftCanvasPts.length >= 3) {
      const pdfPoints = draftCanvasPts.map((p) => canvasToPdfPoint(p.x, p.y, viewport));
      finishWithPdfPoints(drawTool, pdfPoints);
    }
  }, [drawTool, draftCanvasPts, finishWithPdfPoints, viewport]);

  const finishPolyOrCancelCommand = useCallback(() => {
    if (isMultiPointPolyTool(drawTool)) {
      if (dimensionInput.trim()) {
        onDimensionInputChange?.('');
        typedDirectionRef.current = null;
        return;
      }
      setDragCurrent(null);
      if (canFinishDraft(drawTool, draftCanvasPts.length)) {
        finishMultiPointDraft();
      }
      cancelCommand();
      return;
    }
    cancelCommand();
  }, [
    cancelCommand, dimensionInput, draftCanvasPts.length, drawTool,
    finishMultiPointDraft, onDimensionInputChange,
  ]);

  const drawDimensionAnchor = useMemo(() => {
    if (drawTool === DRAW_TOOLS.FREEFORM || drawTool === DRAW_TOOLS.SELECT) return null;
    if (drawTool === DRAW_TOOLS.LINE
      || drawTool === DRAW_TOOLS.RECTANGLE
      || drawTool === DRAW_TOOLS.CIRCLE) {
      return draftCanvasPts.length === 1 ? draftCanvasPts[0] : null;
    }
    return draftCanvasPts.length >= 1 ? draftCanvasPts[draftCanvasPts.length - 1] : null;
  }, [drawTool, draftCanvasPts]);

  const showDimensionInput = Boolean(
    vertexDrag
    || rotateSession?.startAngleRad != null
    || modifyMode === 'offset'
    || (drawTool !== DRAW_TOOLS.SELECT && drawDimensionAnchor),
  );

  const applyRotatePreview = useCallback((degrees, session) => {
    if (!session || session.startAngleRad == null) return;
    const { pivotCanvas, pivotPdf, baseComponent, startAngleRad } = session;
    rotateDegreesRef.current = degrees;
    const aimRad = startAngleRad + (degrees * Math.PI) / 180;
    setRotateAim({
      x: pivotCanvas.x + Math.cos(aimRad) * 48,
      y: pivotCanvas.y + Math.sin(aimRad) * 48,
    });
    setDragPreview(rotateComponent(baseComponent, degrees, {
      pivot: pivotPdf,
      scaleRatio,
    }));
  }, [scaleRatio]);

  const typedLengthLocked = Boolean(
    dimensionInput.trim()
    && supportsTypedLengthLock(drawTool)
    && drawDimensionAnchor,
  );

  const trackDrawPointer = Boolean(
    interactive && !hidden && viewport && drawDimensionAnchor
    && supportsTypedLengthLock(drawTool),
  );

  useEffect(() => {
    if (!trackDrawPointer) return undefined;
    const onMove = (e) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const bounds = canvas.getBoundingClientRect();
      lastPointerCanvasRef.current = {
        x: e.clientX - bounds.left,
        y: e.clientY - bounds.top,
      };
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, [trackDrawPointer]);

  const captureTypedDirection = useCallback(() => {
    if (!supportsTypedLengthLock(drawTool) || !drawDimensionAnchor) return;
    if (typedDirectionRef.current) return;

    const origin = drawDimensionAnchor;
    const raw = lastPointerCanvasRef.current;
    let target = null;

    if (raw && dist(raw, origin) >= 4) {
      target = resolvePoint(raw, origin, { shiftKey: shiftRef.current });
    } else if (dragCurrent && dist(dragCurrent, origin) >= 4) {
      target = resolvePoint(dragCurrent, origin, { shiftKey: shiftRef.current });
    }

    if (!target || dist(target, origin) < 4) {
      const seed = raw || dragCurrent;
      if (seed && dist(seed, origin) >= 4) {
        target = shiftRef.current
          ? applyOrthoLock(origin, seed, true, resolveOrthoAxis(origin, seed))
          : seed;
      } else {
        target = { x: origin.x + 48, y: origin.y };
        if (shiftRef.current) {
          target = applyOrthoLock(origin, target, true, resolveOrthoAxis(origin, seed || target));
        }
      }
    }

    typedDirectionRef.current = target;
  }, [dragCurrent, drawDimensionAnchor, drawTool, resolvePoint]);

  useEffect(() => {
    if (!supportsTypedLengthLock(drawTool) || !drawDimensionAnchor) {
      typedDirectionRef.current = null;
      return;
    }
    if (!dimensionInput.trim()) {
      typedDirectionRef.current = null;
      return;
    }
    captureTypedDirection();
  }, [captureTypedDirection, dimensionInput, drawDimensionAnchor, drawTool]);

  useEffect(() => {
    if (!typedLengthLocked || !typedDirectionRef.current || !drawDimensionAnchor || !viewport) {
      return;
    }
    const distPdf = parseDrawLengthToPdfPoints(dimensionInput, scaleRatio);
    if (!distPdf) return;
    const end = pointAtPdfDistance(
      drawDimensionAnchor,
      typedDirectionRef.current,
      distPdf,
      viewport,
    );
    if (end) setDragCurrent(end);
  }, [
    dimensionInput, drawDimensionAnchor, scaleRatio, typedLengthLocked, viewport,
  ]);

  useEffect(() => {
    if (!rotateSession?.startAngleRad || !dimensionInput.trim()) return;
    const degrees = resolveRotateDegreesFromInput({
      dimensionInput,
      startAngleRad: rotateSession.startAngleRad,
      pivotCanvas: rotateSession.pivotCanvas,
      mouseCanvas: lastPointerCanvasRef.current,
      shiftSnap: shiftRef.current,
    });
    if (degrees == null) return;
    applyRotatePreview(degrees, rotateSession);
  }, [applyRotatePreview, dimensionInput, rotateSession]);

  useEffect(() => {
    if (modifyMode !== 'offset' || !selectedMarkup || offsetHoveredSide == null) return;
    const distancePdf = parseOffsetDistanceInput(dimensionInput, scaleRatio);
    if (!distancePdf) {
      setDragPreview(null);
      return;
    }
    setDragPreview(offsetComponent(
      selectedMarkup,
      distancePdf * offsetHoveredSide,
      { scaleRatio },
    ));
  }, [dimensionInput, modifyMode, offsetHoveredSide, scaleRatio, selectedMarkup]);

  const commitTypedDimension = useCallback(() => {
    if (modifyMode === 'offset' && offsetHoveredSide != null) {
      return applyOffsetSide(offsetHoveredSide);
    }

    if (rotateSession?.startAngleRad != null) {
      const degrees = resolveRotateDegreesFromInput({
        dimensionInput,
        startAngleRad: rotateSession.startAngleRad,
        pivotCanvas: rotateSession.pivotCanvas,
        mouseCanvas: lastPointerCanvasRef.current,
        shiftSnap: shiftRef.current,
      });
      if (degrees == null) return false;
      onRotateUserMarkup?.(degrees, { pivot: rotateSession.pivotPdf });
      setRotateSession(null);
      setRotateAim(null);
      setDragPreview(null);
      rotateDegreesRef.current = 0;
      onDimensionInputChange?.('');
      onModifyModeChange?.(null);
      return true;
    }

    const distPdf = parseDrawLengthToPdfPoints(dimensionInput, scaleRatio);
    if (!distPdf || !viewport) return false;

    if (vertexDrag && editTarget) {
      const anchorCanvas = orthoAnchorForVertex(editTarget, vertexDrag.vertexIndex, viewport);
      const verts = getComponentCanvasVertices(editTarget, viewport);
      const current = verts[vertexDrag.vertexIndex];
      if (!current) return false;
      const origin = anchorCanvas || current;
      const target = dragCurrent || current;
      const newCanvas = anchorCanvas
        ? pointAtPdfDistance(origin, target, distPdf, viewport, { ortho: shiftRef.current })
        : pointAtPdfDistance(
          { x: current.x - 1, y: current.y },
          target,
          distPdf,
          viewport,
        );
      if (!newCanvas) return false;
      const [pdfX, pdfY] = canvasToPdfPoint(newCanvas.x, newCanvas.y, viewport);
      const updated = updateComponentVertex(
        editTarget,
        vertexDrag.vertexIndex,
        [pdfX, pdfY],
        { scaleRatio },
      );
      if (isUserDrawnComponent(editTarget)) onMarkupUpdate?.(updated);
      else onExtractedGeometryUpdate?.(updated);
      onDimensionInputChange?.('');
      return true;
    }

    const anchor = drawDimensionAnchor;
    if (!anchor) return false;
    const dirTarget = typedDirectionRef.current
      || dragCurrent
      || { x: anchor.x + 48, y: anchor.y };

    if (drawTool === DRAW_TOOLS.LINE) {
      const end = pointAtPdfDistance(anchor, dirTarget, distPdf, viewport);
      const pdfPoints = [anchor, end].map((p) => canvasToPdfPoint(p.x, p.y, viewport));
      typedDirectionRef.current = null;
      finishWithPdfPoints(drawTool, pdfPoints);
      onDimensionInputChange?.('');
      return true;
    }

    if (drawTool === DRAW_TOOLS.POLYLINE) {
      const next = pointAtPdfDistance(anchor, dirTarget, distPdf, viewport);
      typedDirectionRef.current = null;
      setDraftCanvasPts((prev) => [...prev, next]);
      setDragCurrent(null);
      onDimensionInputChange?.('');
      return true;
    }

    const target = dragCurrent || { x: anchor.x + 48, y: anchor.y };

    if (drawTool === DRAW_TOOLS.CIRCLE) {
      const rim = pointAtPdfDistance(anchor, target, distPdf, viewport);
      if (dist(rim, anchor) <= 4) return false;
      const pdfPoints = [anchor, rim].map((p) => canvasToPdfPoint(p.x, p.y, viewport));
      finishWithPdfPoints(drawTool, pdfPoints);
      onDimensionInputChange?.('');
      return true;
    }

    if (drawTool === DRAW_TOOLS.RECTANGLE) {
      const end = pointAtPdfDistance(anchor, target, distPdf, viewport, { ortho: shiftRef.current });
      let x0 = Math.min(anchor.x, end.x);
      let y0 = Math.min(anchor.y, end.y);
      let x1 = Math.max(anchor.x, end.x);
      let y1 = Math.max(anchor.y, end.y);
      if (shiftRef.current) {
        const side = Math.max(x1 - x0, y1 - y0);
        x1 = x0 + side;
        y1 = y0 + side;
      }
      const corners = [
        { x: x0, y: y0 },
        { x: x1, y: y0 },
        { x: x1, y: y1 },
        { x: x0, y: y1 },
      ];
      const pdfPoints = corners.map((p) => canvasToPdfPoint(p.x, p.y, viewport));
      finishWithPdfPoints(drawTool, pdfPoints);
      onDimensionInputChange?.('');
      return true;
    }

    if (drawTool === DRAW_TOOLS.POLYLINE || drawTool === DRAW_TOOLS.POLYGON) {
      const next = pointAtPdfDistance(anchor, target, distPdf, viewport, { ortho: shiftRef.current });
      setDraftCanvasPts((prev) => [...prev, next]);
      setDragCurrent(next);
      onDimensionInputChange?.('');
      return true;
    }

    return false;
  }, [
    dimensionInput, drawDimensionAnchor, drawTool, dragCurrent, editTarget,
    finishWithPdfPoints, onDimensionInputChange, onExtractedGeometryUpdate,
    onMarkupUpdate, onModifyModeChange, onRotateUserMarkup, rotateSession,
    applyOffsetSide, modifyMode, offsetHoveredSide,
    scaleRatio, vertexDrag, viewport,
  ]);

  useImperativeHandle(ref, () => ({
    finishDraft: finishMultiPointDraft,
    cancelDraft: resetDraft,
    cancelCommand: finishPolyOrCancelCommand,
    commitTypedDimension,
  }), [commitTypedDimension, finishMultiPointDraft, finishPolyOrCancelCommand, resetDraft]);

  const draftActive = drawTool !== DRAW_TOOLS.SELECT && (
    draftCanvasPts.length > 0
    || freeformPts.length > 0
  );

  useEffect(() => {
    if (modifyMode !== 'rotate') {
      setRotateSession(null);
      setRotateAim(null);
      rotateDegreesRef.current = 0;
    }
    if (modifyMode !== 'mirror') {
      setMirrorSession(null);
      setMirrorAim(null);
    }
    if (modifyMode !== 'offset') {
      setOffsetHandles(null);
      setOffsetHoveredSide(null);
    }
    if (modifyMode !== 'mirror' && modifyMode !== 'offset') {
      copyRef.current = false;
      setCopyModifierActive(false);
    }
  }, [modifyMode]);

  useEffect(() => {
    if (!onDraftChange) return;
    const pointCount = draftCanvasPts.length || freeformPts.length;
    let hint = vertexDrag
      ? 'Drag vertex · Shift = ortho · type length + Enter'
      : draftHintForTool(drawTool, draftCanvasPts.length, arcDrawMode);
    if (
      drawTool === DRAW_TOOLS.SELECT
      && editTarget
      && getRightAngleRectangleCompletion(editTarget)
    ) {
      hint = 'Shift+click open corner to complete rectangle · Shift+click vertex to delete';
    }
    if (modifyMode === 'rotate' && selectedMarkup) {
      hint = rotateSession?.startAngleRad == null
        ? 'Click pivot point, then drag to set angle · Shift = 15° snap'
        : `Rotate ${Math.round(rotateDegreesRef.current)}° · type angle + Enter · Shift = 15° snap`;
    } else if (modifyMode === 'offset' && selectedMarkup) {
      hint = copyModifierActive
        ? 'Hover shape · type distance · click arrow (copy mode)'
        : 'Hover shape · type distance · click arrow · hold C to copy';
    } else if (modifyMode === 'mirror' && selectedMarkup) {
      const copyHint = copyModifierActive ? ' (copy)' : ' · hold C to copy';
      hint = mirrorSession
        ? `Drag mirror axis end${copyHint} · snap to vertices · release to apply`
        : `Click axis start · snap to shape vertices${copyHint}`;
    }
    onDraftChange({
      active: draftActive || drawTool !== DRAW_TOOLS.SELECT || Boolean(vertexDrag)
        || modifyMode === 'rotate' || modifyMode === 'mirror' || modifyMode === 'offset',
      pointCount,
      canFinish: canFinishDraft(drawTool, draftCanvasPts.length),
      hint,
      tool: drawTool,
      arcDrawMode,
      showDimensionInput,
      dimensionPlaceholder: rotateSession?.startAngleRad != null
        ? '45°'
        : modifyMode === 'offset'
          ? (scaleRatio ? "1 or 1'" : 'pt')
          : vertexDrag
            ? (scaleRatio ? "10 or 10'" : 'pt')
            : (scaleRatio ? "10 or 10'" : 'pt'),
      dimensionInputTitle: rotateSession?.startAngleRad != null
        ? 'Type angle in direction of cursor, press Enter'
        : modifyMode === 'offset'
          ? 'Type offset distance, hover arrow and click (or Enter)'
          : 'Type length, press Enter to place',
      copyModifierEnabled: modifyMode === 'mirror' || modifyMode === 'offset' || Boolean(mirrorSession),
    });
  }, [
    onDraftChange, draftActive, draftCanvasPts.length, freeformPts.length, drawTool,
    arcDrawMode, showDimensionInput, vertexDrag, editTarget, modifyMode, rotateSession,
    mirrorSession, selectedMarkup, scaleRatio, copyModifierActive,
  ]);

  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !viewport || width <= 0) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);

    for (const m of pageMarkups) {
      const hideForTransformPreview = (rotateSession || mirrorSession) && dragPreview && !copyModifierActive;
      const hideForOffsetPreview = modifyMode === 'offset' && dragPreview && !copyModifierActive;
      if ((hideForTransformPreview || hideForOffsetPreview) && m.id === selectedMarkupId) continue;
      if (objectDrag?.mode === 'move' && dragPreview && m.id === objectDrag.startComponent.id) continue;
      if (m.id === hoveredMarkupId && m.id !== selectedMarkupId) {
        paintHoverMarkup(ctx, m, viewport);
      }
      paintUserMarkup(ctx, m, viewport, { selected: m.id === selectedMarkupId });
    }

    if (dragPreview && viewport) {
      ctx.save();
      ctx.globalAlpha = 0.55;
      paintUserMarkup(ctx, dragPreview, viewport);
      ctx.restore();
    }

    if (rotateSession?.pivotCanvas) {
      const pivot = rotateSession.pivotCanvas;
      ctx.save();
      ctx.setLineDash([]);
      ctx.strokeStyle = '#2563eb';
      ctx.fillStyle = '#2563eb';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(pivot.x, pivot.y, 5, 0, Math.PI * 2);
      ctx.fill();
      if (rotateAim) {
        ctx.beginPath();
        ctx.moveTo(pivot.x, pivot.y);
        ctx.lineTo(rotateAim.x, rotateAim.y);
        ctx.stroke();
        const deg = Math.round(rotateDegreesRef.current);
        if (Math.abs(deg) >= 1) {
          ctx.font = '600 11px system-ui, sans-serif';
          ctx.fillStyle = '#1e40af';
          ctx.fillText(`${deg}°`, rotateAim.x + 8, rotateAim.y - 8);
        }
      }
      ctx.restore();
    }

    if (mirrorSession?.axisStartCanvas) {
      const start = mirrorSession.axisStartCanvas;
      ctx.save();
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = '#7c3aed';
      ctx.fillStyle = '#7c3aed';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(start.x, start.y, 5, 0, Math.PI * 2);
      ctx.fill();
      if (mirrorAim) {
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(mirrorAim.x, mirrorAim.y);
        ctx.stroke();
        ctx.font = '600 11px system-ui, sans-serif';
        ctx.fillStyle = '#5b21b6';
        ctx.fillText('Mirror axis', mirrorAim.x + 8, mirrorAim.y - 8);
      }
      ctx.restore();
    }

    if (editTarget && drawTool === DRAW_TOOLS.SELECT) {
      const isExtract = !isUserDrawnComponent(editTarget);
      if (isExtract) {
        paintExtractedVertexHandles(ctx, editTarget, viewport, {
          activeIndex: vertexDrag?.vertexIndex ?? -1,
          hoverIndex: hoverVertexIndex,
        });
      } else {
        paintVertexHandles(ctx, editTarget, viewport, {
          activeIndex: vertexDrag?.vertexIndex ?? -1,
          hoverIndex: hoverVertexIndex,
        });
      }
    }

    const previewStroke = 'rgba(233, 112, 36, 0.85)';
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = previewStroke;
    ctx.lineWidth = 2;

    if (drawTool === DRAW_TOOLS.RECTANGLE && draftCanvasPts.length === 1 && dragCurrent) {
      const start = draftCanvasPts[0];
      let x = Math.min(start.x, dragCurrent.x);
      let y = Math.min(start.y, dragCurrent.y);
      let w = Math.abs(dragCurrent.x - start.x);
      let h = Math.abs(dragCurrent.y - start.y);
      if (shiftRef.current) {
        const side = Math.max(w, h);
        w = side;
        h = side;
      }
      ctx.setLineDash([]);
      ctx.strokeRect(x, y, w, h);
      ctx.fillStyle = 'rgba(233, 112, 36, 0.12)';
      ctx.fillRect(x, y, w, h);
      ctx.beginPath();
      ctx.arc(start.x, start.y, 4, 0, Math.PI * 2);
      ctx.fillStyle = previewStroke;
      ctx.fill();
      const wPdf = canvasSegmentLengthPdf({ x, y: y + h / 2 }, { x: x + w, y: y + h / 2 }, viewport);
      const hPdf = canvasSegmentLengthPdf({ x: x + w / 2, y }, { x: x + w / 2, y: y + h }, viewport);
      paintRectDimensions(
        ctx, x, y, w, h,
        formatLiveLength(wPdf, scaleRatio),
        formatLiveLength(hPdf, scaleRatio),
      );
    } else if (drawTool === DRAW_TOOLS.LINE && draftCanvasPts.length === 1 && dragCurrent) {
      const start = draftCanvasPts[0];
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(start.x, start.y);
      ctx.lineTo(dragCurrent.x, dragCurrent.y);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(start.x, start.y, 4, 0, Math.PI * 2);
      ctx.fillStyle = previewStroke;
      ctx.fill();
      const lenPdf = canvasSegmentLengthPdf(start, dragCurrent, viewport);
      paintSegmentDimension(
        ctx, start, dragCurrent, formatLiveLength(lenPdf, scaleRatio),
      );
    } else if (drawTool === DRAW_TOOLS.ARC && draftCanvasPts.length === 1 && dragCurrent) {
      ctx.setLineDash([]);
      if (arcDrawMode === ARC_STYLES.CIRCULAR) {
        const center = draftCanvasPts[0];
        const radius = dist(center, dragCurrent);
        ctx.beginPath();
        ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(233, 112, 36, 0.35)';
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(center.x, center.y);
        ctx.lineTo(dragCurrent.x, dragCurrent.y);
        ctx.strokeStyle = previewStroke;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(center.x, center.y, 4, 0, Math.PI * 2);
        ctx.fillStyle = previewStroke;
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.moveTo(draftCanvasPts[0].x, draftCanvasPts[0].y);
        ctx.lineTo(dragCurrent.x, dragCurrent.y);
        ctx.stroke();
      }
    } else if (drawTool === DRAW_TOOLS.ARC && draftCanvasPts.length === 2 && dragCurrent) {
      if (arcDrawMode === ARC_STYLES.CIRCULAR) {
        const centerPdf = canvasToPdfPoint(draftCanvasPts[0].x, draftCanvasPts[0].y, viewport);
        const startPdf = canvasToPdfPoint(draftCanvasPts[1].x, draftCanvasPts[1].y, viewport);
        const endPdf = canvasToPdfPoint(dragCurrent.x, dragCurrent.y, viewport);
        const draftGeom = buildCircularArcGeometry(centerPdf, startPdf, endPdf);
        paintArcGeometry(ctx, draftGeom, viewport, {
          stroke: previewStroke,
          width: 2,
          dash: [6, 4],
        });
        for (const p of draftCanvasPts) {
          ctx.setLineDash([]);
          ctx.beginPath();
          ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
          ctx.fillStyle = previewStroke;
          ctx.fill();
        }
      } else {
        paintArcBezierPreview(ctx, draftCanvasPts[0], draftCanvasPts[1], dragCurrent);
        for (const p of draftCanvasPts) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
          ctx.fillStyle = previewStroke;
          ctx.fill();
        }
      }
    } else if (drawTool === DRAW_TOOLS.CIRCLE && draftCanvasPts.length === 1 && dragCurrent) {
      const center = draftCanvasPts[0];
      const radius = dist(center, dragCurrent);
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = 'rgba(233, 112, 36, 0.1)';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(center.x, center.y, 4, 0, Math.PI * 2);
      ctx.fillStyle = previewStroke;
      ctx.fill();
      const radiusPdf = canvasSegmentLengthPdf(center, dragCurrent, viewport);
      paintSegmentDimension(
        ctx, center, dragCurrent, formatLiveLength(radiusPdf, scaleRatio),
      );
    } else if (drawTool === DRAW_TOOLS.FREEFORM && freeformPts.length > 1) {
      ctx.beginPath();
      ctx.moveTo(freeformPts[0].x, freeformPts[0].y);
      for (let i = 1; i < freeformPts.length; i += 1) {
        ctx.lineTo(freeformPts[i].x, freeformPts[i].y);
      }
      ctx.stroke();
    } else if (
      draftCanvasPts.length > 0
      && drawTool !== DRAW_TOOLS.LINE
      && drawTool !== DRAW_TOOLS.RECTANGLE
      && drawTool !== DRAW_TOOLS.ARC
      && drawTool !== DRAW_TOOLS.CIRCLE
    ) {
      ctx.beginPath();
      ctx.moveTo(draftCanvasPts[0].x, draftCanvasPts[0].y);
      for (let i = 1; i < draftCanvasPts.length; i += 1) {
        ctx.lineTo(draftCanvasPts[i].x, draftCanvasPts[i].y);
      }
      if (dragCurrent && draftCanvasPts.length >= 1) {
        ctx.lineTo(dragCurrent.x, dragCurrent.y);
      }
      if (drawTool === DRAW_TOOLS.POLYGON && draftCanvasPts.length >= 3) {
        ctx.closePath();
        ctx.fillStyle = 'rgba(233, 112, 36, 0.08)';
        ctx.fill();
      }
      ctx.stroke();
      for (let i = 0; i < draftCanvasPts.length; i += 1) {
        const p = draftCanvasPts[i];
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
        ctx.fillStyle = previewStroke;
        ctx.fill();
        ctx.setLineDash([6, 4]);
        if (i > 0) {
          const lenPdf = canvasSegmentLengthPdf(draftCanvasPts[i - 1], p, viewport);
          paintSegmentDimension(
            ctx, draftCanvasPts[i - 1], p, formatLiveLength(lenPdf, scaleRatio),
          );
        }
      }
      if (dragCurrent) {
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.arc(dragCurrent.x, dragCurrent.y, 3, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(37, 99, 235, 0.8)';
        ctx.lineWidth = 2;
        ctx.stroke();
        if (draftCanvasPts.length >= 1) {
          const last = draftCanvasPts[draftCanvasPts.length - 1];
          const lenPdf = canvasSegmentLengthPdf(last, dragCurrent, viewport);
          paintSegmentDimension(
            ctx, last, dragCurrent, formatLiveLength(lenPdf, scaleRatio),
          );
        }
      }
    }

    if (
      hoverRectangleComplete
      && editTarget
      && drawTool === DRAW_TOOLS.SELECT
      && (shiftRef.current)
    ) {
      const completion = getRightAngleRectangleCompletion(editTarget);
      if (completion) {
        const canvasVerts = getComponentCanvasVertices(editTarget, viewport);
        const [a, , c] = canvasVerts;
        const d = pdfPointToCanvas(completion.fourthPdf[0], completion.fourthPdf[1], viewport);

        ctx.save();
        ctx.strokeStyle = 'rgba(22, 163, 74, 0.85)';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(d.x, d.y);
        ctx.lineTo(c.x, c.y);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.arc(d.x, d.y, 6, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.strokeStyle = '#16a34a';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.restore();
      }
    }

    if (vertexDrag && editTarget && drawTool === DRAW_TOOLS.SELECT) {
      const anchor = orthoAnchorForVertex(editTarget, vertexDrag.vertexIndex, viewport);
      const verts = getComponentCanvasVertices(editTarget, viewport);
      const current = verts[vertexDrag.vertexIndex];
      if (anchor && current) {
        const lenPdf = canvasSegmentLengthPdf(anchor, current, viewport);
        paintSegmentDimension(
          ctx, anchor, current, formatLiveLength(lenPdf, scaleRatio),
        );
      }
    }

    if (snapIndicator) {
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.arc(snapIndicator.x, snapIndicator.y, 6, 0, Math.PI * 2);
      ctx.strokeStyle = '#2563eb';
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    if (modifyMode === 'offset' && offsetHandles && drawTool === DRAW_TOOLS.SELECT) {
      const { anchorCanvas, arrows } = offsetHandles;
      ctx.save();
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = 'rgba(37, 99, 235, 0.55)';
      ctx.lineWidth = 1.5;
      for (const arrow of arrows) {
        ctx.beginPath();
        ctx.moveTo(anchorCanvas.x, anchorCanvas.y);
        ctx.lineTo(arrow.x, arrow.y);
        ctx.stroke();
        paintOffsetArrow(ctx, arrow.x, arrow.y, arrow.angle, arrow.side === offsetHoveredSide);
      }
      ctx.restore();
    }

    ctx.setLineDash([]);
  }, [
    width, height, viewport, pageMarkups, selectedMarkupId,
    hoveredMarkupId, drawTool, arcDrawMode, dragCurrent, draftCanvasPts, editTarget,
    freeformPts, snapIndicator, vertexDrag, hoverRectangleComplete, hoverVertexIndex, dragPreview, scaleRatio,
    rotateSession, rotateAim, mirrorSession, mirrorAim, objectDrag, selectedMarkupId,
    modifyMode, offsetHandles, offsetHoveredSide, copyModifierActive,
  ]);

  useEffect(() => {
    paint();
  }, [paint, hidden]);

  const localPoint = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const bounds = canvas.getBoundingClientRect();
    return { x: e.clientX - bounds.left, y: e.clientY - bounds.top };
  }, []);

  const handlePointerDown = useCallback((e) => {
    if (!interactive || !viewport || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const raw = localPoint(e);
    if (!raw) return;
    lastPointerCanvasRef.current = raw;

    try {
      canvasRef.current?.setPointerCapture(e.pointerId);
    } catch (_) {
      // ignore
    }

    if (drawTool === DRAW_TOOLS.SELECT) {
      const shift = e.shiftKey;
      const ctrl = e.ctrlKey || e.metaKey;
      const alt = e.altKey;
      const hitMarkup = findHitMarkup(pageMarkups, raw.x, raw.y, viewport);

      if (modifyMode === 'offset' && selectedMarkup && offsetHandles) {
        const hit = hitTestOffsetArrow(raw.x, raw.y, offsetHandles);
        if (hit) {
          applyOffsetSide(hit.side);
          return;
        }
      }

      if (
        modifyMode === 'rotate'
        && selectedMarkup
        && isUserDrawnComponent(selectedMarkup)
        && !rotateSession
      ) {
        const pt = resolvePoint(raw, null);
        const pivotPdf = canvasToPdfPoint(pt.x, pt.y, viewport);
        setRotateSession({
          pivotCanvas: pt,
          pivotPdf,
          startAngleRad: null,
          baseComponent: selectedMarkup,
        });
        rotateDegreesRef.current = 0;
        setDragPreview(null);
        setRotateAim(null);
        return;
      }

      if (
        modifyMode === 'mirror'
        && selectedMarkup
        && isUserDrawnComponent(selectedMarkup)
        && !mirrorSession
      ) {
        const pt = resolveMirrorPoint(raw);
        const axisStartPdf = canvasToPdfPoint(pt.x, pt.y, viewport);
        setMirrorSession({
          axisStartCanvas: pt,
          axisStartPdf,
          baseComponent: selectedMarkup,
        });
        setDragPreview(null);
        setMirrorAim(null);
        return;
      }

      if (shift && editTarget && componentSupportsVertexAddRemove(editTarget)) {
        const onPolylineBody = hitTestUserMarkup(raw.x, raw.y, editTarget, viewport);
        if (
          !onPolylineBody
          && isPointInRectangleCompletionZone(raw.x, raw.y, editTarget, viewport)
        ) {
          onMarkupHistoryCheckpoint?.();
          const updated = completeOpenPolylineToRectangle(editTarget, { scaleRatio });
          if (isUserDrawnComponent(editTarget)) onMarkupUpdate?.(updated);
          else onExtractedGeometryUpdate?.(updated);
          return;
        }

        const vi = hitTestComponentVertex(raw.x, raw.y, editTarget, viewport);
        if (vi >= 0) {
          onMarkupHistoryCheckpoint?.();
          const updated = removeVertexAtIndex(editTarget, vi, { scaleRatio });
          if (isUserDrawnComponent(editTarget)) onMarkupUpdate?.(updated);
          else onExtractedGeometryUpdate?.(updated);
          return;
        }
        const edge = hitTestComponentEdge(raw.x, raw.y, editTarget, viewport);
        if (edge >= 0) {
          const pt = resolvePoint(raw, null);
          const [pdfX, pdfY] = canvasToPdfPoint(pt.x, pt.y, viewport);
          onMarkupHistoryCheckpoint?.();
          const updated = insertVertexOnEdge(editTarget, edge, [pdfX, pdfY], { scaleRatio });
          if (isUserDrawnComponent(editTarget)) onMarkupUpdate?.(updated);
          else onExtractedGeometryUpdate?.(updated);
          return;
        }
      }

      if (editTarget && !shift) {
        const vi = hitTestComponentVertex(raw.x, raw.y, editTarget, viewport);
        if (vi >= 0) {
          onMarkupHistoryCheckpoint?.();
          setVertexDrag({ vertexIndex: vi, pointerId: e.pointerId });
          return;
        }
      }

      if (alt && hitMarkup && isUserDrawnComponent(hitMarkup)) {
        onMarkupSelect?.(hitMarkup);
        const [ox, oy] = canvasToPdfPoint(raw.x, raw.y, viewport);
        onMarkupHistoryCheckpoint?.();
        setObjectDrag({
          mode: 'copy',
          originPdf: [ox, oy],
          startComponent: hitMarkup,
        });
        return;
      }

      const moveTarget = hitMarkup && isUserDrawnComponent(hitMarkup) ? hitMarkup : null;
      const wantsMove = ctrl || (modifyMode === 'move' && editTarget
        && editTarget.id === hitMarkup?.id
        && hitTestUserMarkup(raw.x, raw.y, editTarget, viewport));

      if (wantsMove && moveTarget) {
        onMarkupSelect?.(moveTarget);
        const [ox, oy] = canvasToPdfPoint(raw.x, raw.y, viewport);
        onMarkupHistoryCheckpoint?.();
        setObjectDrag({
          mode: 'move',
          originPdf: [ox, oy],
          startComponent: moveTarget,
        });
        return;
      }

      if (hitMarkup) {
        onMarkupSelect?.(hitMarkup);
        return;
      }
      onMarkupSelect?.(null);
      return;
    }

    const origin = draftCanvasPts.length
      ? draftCanvasPts[draftCanvasPts.length - 1]
      : null;
    const pt = resolvePoint(raw, origin);

    if (drawTool === DRAW_TOOLS.LINE || drawTool === DRAW_TOOLS.RECTANGLE || drawTool === DRAW_TOOLS.CIRCLE) {
      if (draftCanvasPts.length === 0) {
        setDraftCanvasPts([pt]);
        setDragCurrent(null);
        return;
      }

      const start = draftCanvasPts[0];
      const end = resolvePoint(raw, start);

      if (drawTool === DRAW_TOOLS.CIRCLE) {
        if (dist(start, end) > 4) {
          const pdfPoints = [start, end].map((p) => canvasToPdfPoint(p.x, p.y, viewport));
          finishWithPdfPoints(drawTool, pdfPoints);
        } else {
          resetDraft();
        }
        return;
      }

      if (drawTool === DRAW_TOOLS.LINE) {
        if (dist(start, end) > 4) {
          const pdfPoints = [start, end].map((p) => canvasToPdfPoint(p.x, p.y, viewport));
          finishWithPdfPoints(drawTool, pdfPoints);
        } else {
          resetDraft();
        }
        return;
      }

      if (dist(start, end) > 4) {
        let x0 = Math.min(start.x, end.x);
        let y0 = Math.min(start.y, end.y);
        let x1 = Math.max(start.x, end.x);
        let y1 = Math.max(start.y, end.y);
        if (shiftRef.current) {
          const side = Math.max(x1 - x0, y1 - y0);
          x1 = x0 + side;
          y1 = y0 + side;
        }
        const corners = [
          { x: x0, y: y0 },
          { x: x1, y: y0 },
          { x: x1, y: y1 },
          { x: x0, y: y1 },
        ];
        const pdfPoints = corners.map((p) => canvasToPdfPoint(p.x, p.y, viewport));
        finishWithPdfPoints(drawTool, pdfPoints);
      } else {
        resetDraft();
      }
      return;
    }

    drawingRef.current = true;
    const drawPt = resolvePoint(raw, origin);

    if (drawTool === DRAW_TOOLS.FREEFORM) {
      setFreeformPts([drawPt]);
      return;
    }

    if (drawTool === DRAW_TOOLS.POLYLINE || drawTool === DRAW_TOOLS.POLYGON || drawTool === DRAW_TOOLS.ARC) {
      if (drawTool === DRAW_TOOLS.ARC && draftCanvasPts.length === 2) {
        const all = [...draftCanvasPts, drawPt];
        const pdfPoints = all.map((p) => canvasToPdfPoint(p.x, p.y, viewport));
        finishWithPdfPoints(drawTool, pdfPoints);
        return;
      }

      if (drawTool === DRAW_TOOLS.ARC && draftCanvasPts.length >= 3) {
        resetDraft();
        setDraftCanvasPts([drawPt]);
        return;
      }

      setDraftCanvasPts((prev) => [...prev, drawPt]);
      if (drawTool === DRAW_TOOLS.POLYLINE) {
        setDragCurrent(null);
      }
    }
  }, [
    drawTool, draftCanvasPts, editTarget, finishWithPdfPoints, interactive,
    localPoint, modifyMode, offsetHandles, applyOffsetSide, resolveMirrorPoint,
    onMarkupHistoryCheckpoint, onMarkupSelect, pageMarkups, resetDraft,
    resolvePoint, rotateSession, mirrorSession, selectedMarkup, viewport,
  ]);

  const handlePointerMove = useCallback((e) => {
    if (!interactive || !viewport) return;
    const raw = localPoint(e);
    if (!raw) return;
    lastPointerCanvasRef.current = raw;

    if (modifyMode === 'offset' && selectedMarkup && viewport) {
      const onObject = hitTestUserMarkup(raw.x, raw.y, selectedMarkup, viewport);
      if (onObject) {
        const handles = getOffsetArrowHandles(selectedMarkup, raw.x, raw.y, viewport);
        setOffsetHandles(handles);
        const side = hoverOffsetArrowSide(raw.x, raw.y, handles);
        setOffsetHoveredSide(side);
        const distancePdf = parseOffsetDistanceInput(dimensionInput, scaleRatio);
        if (distancePdf && side != null) {
          setDragPreview(offsetComponent(selectedMarkup, distancePdf * side, { scaleRatio }));
        } else if (!side) {
          setDragPreview(null);
        }
        return;
      }
      setOffsetHandles(null);
      setOffsetHoveredSide(null);
      setDragPreview(null);
      return;
    }

    if (rotateSession && viewport) {
      const { pivotCanvas, pivotPdf, baseComponent } = rotateSession;
      let { startAngleRad } = rotateSession;
      if (startAngleRad == null) {
        if (dist(raw, pivotCanvas) < 8) return;
        startAngleRad = Math.atan2(raw.y - pivotCanvas.y, raw.x - pivotCanvas.x);
        onDimensionInputChange?.('');
        setRotateSession((s) => ({ ...s, startAngleRad }));
      }

      const session = { ...rotateSession, pivotCanvas, pivotPdf, baseComponent, startAngleRad };
      const typedDegrees = resolveRotateDegreesFromInput({
        dimensionInput,
        startAngleRad,
        pivotCanvas,
        mouseCanvas: raw,
        shiftSnap: shiftRef.current,
      });

      let degrees;
      if (typedDegrees != null) {
        degrees = typedDegrees;
      } else {
        let currAngle = Math.atan2(raw.y - pivotCanvas.y, raw.x - pivotCanvas.x);
        let deltaRad = currAngle - startAngleRad;
        if (shiftRef.current) {
          const snap = Math.PI / 12;
          deltaRad = Math.round(deltaRad / snap) * snap;
        }
        degrees = (deltaRad * 180) / Math.PI;
      }

      applyRotatePreview(degrees, session);
      return;
    }

    if (mirrorSession && viewport) {
      const { axisStartCanvas, axisStartPdf, baseComponent } = mirrorSession;
      if (dist(raw, axisStartCanvas) < 8) return;
      const pt = resolveMirrorPoint(raw);
      const axisEndPdf = canvasToPdfPoint(pt.x, pt.y, viewport);
      setMirrorAim(pt);
      setDragPreview(mirrorComponent(baseComponent, axisStartPdf, axisEndPdf, {
        scaleRatio,
        splitPolyline: true,
      }));
      return;
    }

    if (vertexDrag && editTarget) {
      let origin = null;
      if (e.shiftKey) {
        origin = orthoAnchorForVertex(editTarget, vertexDrag.vertexIndex, viewport);
      }
      const pt = resolvePoint(raw, origin, { shiftKey: e.shiftKey });
      setDragCurrent(pt);
      const [pdfX, pdfY] = canvasToPdfPoint(pt.x, pt.y, viewport);
      const updated = updateComponentVertex(
        editTarget,
        vertexDrag.vertexIndex,
        [pdfX, pdfY],
        { scaleRatio },
      );
      if (isUserDrawnComponent(editTarget)) onMarkupUpdate?.(updated);
      else onExtractedGeometryUpdate?.(updated);
      return;
    }

    if (objectDrag) {
      const pt = resolvePoint(raw, null);
      const [cx, cy] = canvasToPdfPoint(pt.x, pt.y, viewport);
      const dx = cx - objectDrag.originPdf[0];
      const dy = cy - objectDrag.originPdf[1];
      const translated = translateComponentPdfVertices(
        objectDrag.startComponent,
        dx,
        dy,
        { scaleRatio },
      );

      if (objectDrag.mode === 'copy') {
        setDragPreview(translated);
        return;
      }

      onMarkupUpdate?.({ ...translated, id: objectDrag.startComponent.id });
      return;
    }

    if (drawTool === DRAW_TOOLS.SELECT) {
      if (editTarget) {
        const vi = hitTestComponentVertex(raw.x, raw.y, editTarget, viewport);
        setHoverVertexIndex(vi);
      } else {
        setHoverVertexIndex(-1);
      }
      const hitMarkup = findHitMarkup(pageMarkups, raw.x, raw.y, viewport);
      setHoveredMarkupId(hitMarkup?.id || null);
      setHoverMarkupMove(Boolean(
        hitMarkup
        && isUserDrawnComponent(hitMarkup)
        && (hitTestComponentVertex(raw.x, raw.y, hitMarkup, viewport) < 0),
      ));
      const inRectZone = Boolean(
        editTarget
        && (e.shiftKey || shiftRef.current)
        && isPointInRectangleCompletionZone(raw.x, raw.y, editTarget, viewport),
      );
      setHoverRectangleComplete(inRectZone);
      if (inRectZone) {
        const completion = getRightAngleRectangleCompletion(editTarget);
        if (completion) {
          setSnapIndicator(pdfPointToCanvas(
            completion.fourthPdf[0],
            completion.fourthPdf[1],
            viewport,
          ));
        }
        return;
      }
      if (!hitMarkup) setSnapIndicator(null);
      else resolvePoint(raw, null);
      return;
    }

    if (typedLengthLocked) {
      return;
    }

    const origin = (drawTool === DRAW_TOOLS.LINE
      || drawTool === DRAW_TOOLS.RECTANGLE
      || drawTool === DRAW_TOOLS.CIRCLE)
      ? draftCanvasPts[0]
      : (draftCanvasPts.length ? draftCanvasPts[draftCanvasPts.length - 1] : null);
    const pt = resolvePoint(raw, origin);

    if (
      (drawTool === DRAW_TOOLS.RECTANGLE
        || drawTool === DRAW_TOOLS.LINE
        || drawTool === DRAW_TOOLS.CIRCLE)
      && draftCanvasPts.length === 1
    ) {
      setDragCurrent(pt);
      return;
    }

    if (drawTool === DRAW_TOOLS.FREEFORM && freeformPts.length > 0 && drawingRef.current) {
      setFreeformPts((prev) => {
        const last = prev[prev.length - 1];
        if (last && dist(last, pt) < 3) return prev;
        return [...prev, pt];
      });
    } else if (draftCanvasPts.length > 0) {
      setDragCurrent(pt);
    } else {
      resolvePoint(raw, null);
    }
  }, [
    drawTool, draftCanvasPts, editTarget, freeformPts.length, interactive,
    localPoint, objectDrag, onExtractedGeometryUpdate, onMarkupUpdate, pageMarkups,
    resolvePoint, rotateSession, mirrorSession, scaleRatio, typedLengthLocked,
    applyRotatePreview, dimensionInput, onDimensionInputChange, resolveMirrorPoint,
    modifyMode, selectedMarkup, offsetHandles,
    vertexDrag, viewport,
  ]);

  const handlePointerUp = useCallback((e) => {
    if (!interactive || !viewport || e.button !== 0) return;

    if (rotateSession) {
      const { pivotPdf, startAngleRad } = rotateSession;
      if (startAngleRad != null && Math.abs(rotateDegreesRef.current) >= 0.05) {
        onRotateUserMarkup?.(rotateDegreesRef.current, { pivot: pivotPdf });
      }
      setRotateSession(null);
      setRotateAim(null);
      setDragPreview(null);
      rotateDegreesRef.current = 0;
      onModifyModeChange?.(null);
      return;
    }

    if (mirrorSession) {
      const { axisStartPdf, axisStartCanvas } = mirrorSession;
      const raw = localPoint(e);
      if (raw && dist(raw, axisStartCanvas) >= 8) {
        const pt = resolveMirrorPoint(raw);
        const axisEndPdf = canvasToPdfPoint(pt.x, pt.y, viewport);
        onMirrorUserMarkup?.(axisStartPdf, axisEndPdf, { copy: copyRef.current });
      }
      setMirrorSession(null);
      setMirrorAim(null);
      setDragPreview(null);
      onModifyModeChange?.(null);
      return;
    }

    if (vertexDrag) {
      setVertexDrag(null);
      setDragCurrent(null);
      onDimensionInputChange?.('');
      return;
    }

    if (objectDrag) {
      if (objectDrag.mode === 'copy') {
        const base = dragPreview || objectDrag.startComponent;
        const src = objectDrag.startComponent;
        const copy = {
          ...base,
          id: newUserMarkupId(),
          label: src.label || src.kind || 'User markup (copy)',
          kind: src.kind || '',
          taxonomyAxis: src.taxonomyAxis,
          mappingStatus: src.kind
            ? (src.mappingStatus || 'user_confirmed')
            : 'pending_user_confirm',
          createdAt: new Date().toISOString(),
        };
        onMarkupComplete?.(copy);
        onMarkupSelect?.(copy);
      }
      setDragPreview(null);
      setObjectDrag(null);
      return;
    }

    if (drawTool === DRAW_TOOLS.FREEFORM && freeformPts.length > 2) {
      const pdfPoints = freeformPts.map((p) => canvasToPdfPoint(p.x, p.y, viewport));
      finishWithPdfPoints(drawTool, pdfPoints);
      resetDraft();
    }
  }, [
    drawTool, finishWithPdfPoints, freeformPts, interactive, dragPreview,
    objectDrag, onMarkupComplete, onMarkupHistoryCheckpoint, onMarkupSelect,
    onModifyModeChange, onMirrorUserMarkup, onRotateUserMarkup, resetDraft,
    rotateSession, mirrorSession, localPoint, vertexDrag, viewport, resolveMirrorPoint,
  ]);

  useEffect(() => {
    resetDraft();
    setVertexDrag(null);
    setHoverRectangleComplete(false);
  }, [drawTool, arcDrawMode, pageIndex, resetDraft]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Shift') shiftRef.current = true;
      const inInput = e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA';
      const copyModeActive = modifyMode === 'mirror' || modifyMode === 'offset' || Boolean(mirrorSession);
      if ((e.key === 'c' || e.key === 'C') && copyModeActive) {
        e.preventDefault();
        e.stopPropagation();
        copyRef.current = true;
        setCopyModifierActive(true);
        return;
      }

      if (e.key === 'Tab' && snapEnabled && drawTool !== DRAW_TOOLS.SELECT) {
        e.preventDefault();
        const canvas = canvasRef.current;
        if (!canvas || !dragCurrent) return;
        const cycled = resolvePoint(dragCurrent, null, { cycleSnap: true });
        if (cycled) setDragCurrent(cycled);
        return;
      }

      if (
        drawTool === DRAW_TOOLS.SELECT
        && (vertexDrag || rotateSession?.startAngleRad != null
          || (modifyMode === 'offset' && offsetHoveredSide != null))
        && showDimensionInput
        && !inInput
      ) {
        if (e.key === 'Enter' && dimensionInput.trim()) {
          e.preventDefault();
          commitTypedDimension();
          return;
        }
        if (e.key === 'Backspace' && dimensionInput) {
          e.preventDefault();
          onDimensionInputChange?.(dimensionInput.slice(0, -1));
          return;
        }
        const angleChar = rotateSession?.startAngleRad != null
          ? /[\d.\-°]/
          : modifyMode === 'offset'
            ? /[\d.'"]/
            : /[\d'".\- ]/;
        if (e.key.length === 1 && angleChar.test(e.key)) {
          e.preventDefault();
          onDimensionInputChange?.(dimensionInput + e.key);
          return;
        }
      }

      if (inInput) return;

      if (drawTool === DRAW_TOOLS.SELECT && !inInput) {
        if (e.key === 'Escape') {
          e.preventDefault();
          if (objectDrag) {
            if (objectDrag.mode === 'move') {
              onMarkupUpdate?.({
                ...objectDrag.startComponent,
                id: objectDrag.startComponent.id,
              });
            }
            setObjectDrag(null);
            setDragPreview(null);
            return;
          }
          if (vertexDrag) {
            setVertexDrag(null);
            setDragCurrent(null);
            onDimensionInputChange?.('');
            return;
          }
          if (rotateSession?.startAngleRad != null && dimensionInput.trim()) {
            onDimensionInputChange?.('');
            return;
          }
          if (rotateSession?.startAngleRad != null) {
            setRotateSession(null);
            setRotateAim(null);
            setDragPreview(null);
            rotateDegreesRef.current = 0;
            onDimensionInputChange?.('');
            return;
          }
          if (
            mirrorSession
            || rotateSession
            || modifyMode === 'mirror'
            || modifyMode === 'rotate'
            || modifyMode === 'move'
            || modifyMode === 'offset'
          ) {
            cancelCommand();
          }
        }
      }

      if (drawTool !== DRAW_TOOLS.SELECT) {
        if (showDimensionInput && e.key.length === 1 && /[\d'".\- ]/.test(e.key)) {
          e.preventDefault();
          onDimensionInputChange?.(dimensionInput + e.key);
          return;
        }
        if (showDimensionInput && e.key === 'Backspace' && dimensionInput) {
          e.preventDefault();
          onDimensionInputChange?.(dimensionInput.slice(0, -1));
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          finishPolyOrCancelCommand();
        } else if (e.key === 'Enter' || e.key === ' ') {
          if (dimensionInput.trim() && commitTypedDimension()) {
            e.preventDefault();
            return;
          }
          if (drawTool === DRAW_TOOLS.POLYLINE || drawTool === DRAW_TOOLS.POLYGON) {
            e.preventDefault();
            finishMultiPointDraft();
          }
        } else if (e.key === 'Backspace' && draftCanvasPts.length > 0
          && drawTool !== DRAW_TOOLS.LINE
          && drawTool !== DRAW_TOOLS.RECTANGLE
          && drawTool !== DRAW_TOOLS.CIRCLE
          && !dimensionInput) {
          e.preventDefault();
          setDraftCanvasPts((prev) => prev.slice(0, -1));
          setDragCurrent(null);
        }
      }
    };
    const onKeyUp = (e) => {
      if (e.key === 'Shift') {
        shiftRef.current = false;
        orthoAxisRef.current = null;
        orthoAxisOriginRef.current = null;
        setHoverRectangleComplete(false);
      }
      if (e.key === 'c' || e.key === 'C') {
        copyRef.current = false;
        setCopyModifierActive(false);
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
    };
  }, [
    cancelCommand, finishPolyOrCancelCommand, commitTypedDimension, dimensionInput, drawTool,
    finishMultiPointDraft, modifyMode, onDimensionInputChange, onMarkupUpdate, onModifyModeChange,
    offsetHoveredSide, resetDraft, resolvePoint, rotateSession, mirrorSession, showDimensionInput,
    snapEnabled, vertexDrag, objectDrag,
  ]);

  if (width <= 0 || height <= 0) return null;

  let cursor = 'crosshair';
  if (drawTool === DRAW_TOOLS.SELECT) {
    if (objectDrag || vertexDrag || rotateSession || mirrorSession) {
      cursor = 'grabbing';
    } else if (modifyMode === 'rotate' || modifyMode === 'mirror') {
      cursor = 'crosshair';
    } else if (modifyMode === 'offset') {
      cursor = offsetHoveredSide != null ? 'pointer' : 'default';
    } else if (hoverVertexIndex >= 0) {
      cursor = 'grab';
    } else if (hoverMarkupMove) {
      cursor = 'move';
    } else {
      cursor = 'default';
    }
  }

  return (
    <Layer
      ref={canvasRef}
      width={width}
      height={height}
      $interactive={interactive && !hidden}
      $cursor={cursor}
      style={{ visibility: hidden ? 'hidden' : 'visible' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => {
        resetDraft();
        setVertexDrag(null);
        setHoverRectangleComplete(false);
        setObjectDrag(null);
        setDragPreview(null);
        setRotateSession(null);
        setRotateAim(null);
        setMirrorSession(null);
        setMirrorAim(null);
        onModifyModeChange?.(null);
      }}
    />
  );
});

export default UserDrawLayer;
